import { cloneRgba, type Rgba } from '../color.ts';
import { pointInSolid } from '../collision.ts';
import { clampToBounds, clonePoint, distanceSquared } from './document.ts';
import { pointInPolygon } from './islands.ts';
import { DEFAULT_START_AREA_COLOR } from './palette.ts';
import {
  clearEdgeBend,
  findBendHandleAt,
  findRingEdgeAt,
  moveBendHandle,
  moveRingVertex,
  removeRingVertex,
  setCornerRadius,
  setEdgeBend,
  tessellateRing,
} from './ring.ts';
import {
  DEFAULT_CORNER_RADIUS,
  MIN_RING_VERTICES,
  type MapDocument,
  type StartArea,
  type Vec2,
} from './types.ts';

/** Replaces any existing start area with a new one built from these vertices. */
export function setStartArea(map: MapDocument, vertices: readonly Vec2[]): StartArea {
  if (vertices.length < MIN_RING_VERTICES) {
    throw new Error(`The start area needs at least ${MIN_RING_VERTICES} vertices`);
  }

  const area: StartArea = {
    id: 'start-area',
    color: cloneRgba(map.startArea?.color ?? DEFAULT_START_AREA_COLOR),
    vertices: vertices.map((vertex) => clampToBounds(clonePoint(vertex), map.bounds)),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius: DEFAULT_CORNER_RADIUS,
  };

  map.startArea = area;
  return area;
}

/**
 * Where the boat starts looking, and where it is dropped outright on a map with no start area.
 * The plain vertex average is enough: the ring is convex in practice and this only has to land
 * somewhere sensible inside it, not at the true area centroid.
 */
export function startAreaCentroid(map: MapDocument): Vec2 {
  const vertices = map.startArea?.vertices;

  if (!vertices || vertices.length === 0) {
    return { x: map.bounds.width / 2, y: map.bounds.height / 2 };
  }

  const total = vertices.reduce((acc, vertex) => ({ x: acc.x + vertex.x, y: acc.y + vertex.y }), {
    x: 0,
    y: 0,
  });

  return { x: total.x / vertices.length, y: total.y / vertices.length };
}

export function clearStartArea(map: MapDocument): boolean {
  if (!map.startArea) {
    return false;
  }

  map.startArea = null;
  return true;
}

export function findStartAreaAt(map: MapDocument, point: Vec2): StartArea | undefined {
  if (!map.startArea) {
    return undefined;
  }

  return pointInPolygon(point, tessellateRing(map.startArea)) ? map.startArea : undefined;
}

/** Inside the start polygon and not on an island, a rock, or outside the map. */
export function isLegalStartPlacement(map: MapDocument, point: Vec2): boolean {
  return findStartAreaAt(map, point) !== undefined && !pointInSolid(map, point);
}

/**
 * Test drive may plant on any open water, including outside the start area.
 * Land and the void stay illegal, same as a catalog start.
 */
export function isLegalTestPlacement(map: MapDocument, point: Vec2): boolean {
  return !pointInSolid(map, point);
}

export function findStartAreaVertexAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
): number | null {
  if (!map.startArea) {
    return null;
  }

  const radiusSq = radius * radius;
  let best: { index: number; dist: number } | null = null;

  for (let index = 0; index < map.startArea.vertices.length; index += 1) {
    const vertex = map.startArea.vertices[index];

    if (!vertex) {
      continue;
    }

    const dist = distanceSquared(point, vertex);

    if (dist <= radiusSq && (!best || dist < best.dist)) {
      best = { index, dist };
    }
  }

  return best?.index ?? null;
}

export function moveStartAreaVertex(map: MapDocument, index: number, point: Vec2): boolean {
  if (!map.startArea) {
    return false;
  }

  return moveRingVertex(map.startArea, index, clampToBounds(point, map.bounds));
}

export function removeStartAreaVertex(map: MapDocument, index: number): boolean {
  if (!map.startArea || map.startArea.vertices[index] === undefined) {
    return false;
  }

  if (map.startArea.vertices.length <= MIN_RING_VERTICES) {
    return false;
  }

  return removeRingVertex(map.startArea, index);
}

export function setStartAreaCornerRadius(map: MapDocument, radius: number): boolean {
  if (!map.startArea) {
    return false;
  }

  setCornerRadius(map.startArea, radius);
  return true;
}

export function setStartAreaColor(map: MapDocument, color: Rgba): boolean {
  if (!map.startArea) {
    return false;
  }

  map.startArea.color = cloneRgba(color);
  return true;
}

export function findStartAreaEdgeAt(map: MapDocument, point: Vec2, radius: number): number | null {
  return map.startArea ? findRingEdgeAt(map.startArea, point, radius) : null;
}

export function findStartAreaBendAt(map: MapDocument, point: Vec2, radius: number): number | null {
  return map.startArea ? findBendHandleAt(map.startArea, point, radius) : null;
}

export function setStartAreaEdgeBend(map: MapDocument, edgeIndex: number, point: Vec2): boolean {
  return map.startArea ? setEdgeBend(map.startArea, edgeIndex, clampToBounds(point, map.bounds)) : false;
}

export function moveStartAreaBend(map: MapDocument, edgeIndex: number, point: Vec2): boolean {
  return map.startArea
    ? moveBendHandle(map.startArea, edgeIndex, clampToBounds(point, map.bounds))
    : false;
}

export function clearStartAreaEdgeBend(map: MapDocument, edgeIndex: number): boolean {
  return map.startArea ? clearEdgeBend(map.startArea, edgeIndex) : false;
}
